/**
 * Endpoints del grupo **Avanzado** de Configuración: máquinas, lados de máquina
 * y mangueras del controlador.
 *
 * Los lados van con el grupo `configuration`; máquinas y mangueras con `admin`,
 * como en el panel anterior (`Maquina::index()` y `Maquina::Nozzles()` pedían
 * `in_group(1)`), porque escriben la tabla que lee Monitoreo en vivo. La
 * **automatización de RIC** también es de `admin` (`Settings::ric_automation()`).
 *
 * Las fotos viajan en binario (`POST /api/maquinas/:id/foto`) porque el cliente
 * HTTP envía JSON, y se muestran con `urlFotoMaquina()` / `urlFotoLado()`, que
 * apuntan a las rutas públicas del módulo.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { env } from '@/config/env';
import { apiClient } from '@/lib/http/api-client';
import { getSessionProvider } from '@/lib/http/session-provider';

import type {
  EstadoSincronizacion,
  Lado,
  LadoPayload,
  LadosRespuesta,
  MangueraLadoPayload,
  ManguerasLadoRespuesta,
  ManguerasRespuesta,
  Maquina,
  MaquinaPayload,
  MaquinasRespuesta,
  RicPayload,
  RicRespuesta,
  SincronizacionRespuesta,
  TanqueRic,
} from './avanzado.types';

export const avanzadoKeys = {
  maquinas: ['avanzado', 'maquinas'] as const,
  lados: ['avanzado', 'lados'] as const,
  manguerasLado: (id: number) => ['avanzado', 'lados', id, 'mangueras'] as const,
  mangueras: ['avanzado', 'mangueras'] as const,
  estadoSync: ['avanzado', 'mangueras', 'estado'] as const,
  ric: ['avanzado', 'ric-automation'] as const,
} as const;

/** URL de la foto de una máquina o de un lado (ruta pública, sin token). */
function urlFoto(recurso: 'maquinas' | 'lados', id: number, version: string | null): string {
  const sufijo = version ? `?v=${encodeURIComponent(version)}` : '';

  return `${env.apiUrl}/api/${recurso}/foto/${id}${sufijo}`;
}

export function urlFotoMaquina(id: number, version: string | null): string {
  return urlFoto('maquinas', id, version);
}

export function urlFotoLado(id: number, version: string | null): string {
  return urlFoto('lados', id, version);
}

/** Sube una foto. Con `fetch` directo porque el cuerpo es el archivo (no JSON). */
async function subirFoto(
  recurso: 'maquinas' | 'lados',
  id: number,
  archivo: File,
): Promise<{ foto: string; version: string }> {
  const token = getSessionProvider()?.getAccessToken();
  const respuesta = await fetch(`${env.apiUrl}/api/${recurso}/${id}/foto`, {
    method: 'POST',
    headers: {
      'Content-Type': archivo.type || 'image/png',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: archivo,
  });

  const contenido = (await respuesta.json().catch(() => null)) as
    | { status: boolean; response?: { foto: string; version: string }; error?: string }
    | null;

  if (!respuesta.ok || !contenido?.status || !contenido.response) {
    throw new Error(contenido?.error ?? 'No se pudo subir la foto.');
  }

  return contenido.response;
}

export const avanzadoApi = {
  // Máquinas (`maquina`)
  listarMaquinas: (signal?: AbortSignal): Promise<MaquinasRespuesta> =>
    apiClient.get<MaquinasRespuesta>('/api/maquinas', { signal }),
  crearMaquina: (payload: MaquinaPayload): Promise<{ maquina: Maquina; mensaje: string }> =>
    apiClient.post<{ maquina: Maquina; mensaje: string }>('/api/maquinas', { body: payload }),
  actualizarMaquina: (
    id: number,
    payload: MaquinaPayload,
  ): Promise<{ maquina: Maquina; mensaje: string }> =>
    apiClient.put<{ maquina: Maquina; mensaje: string }>(`/api/maquinas/${id}`, { body: payload }),
  cambiarEstadoMaquina: (id: number, activo: boolean): Promise<{ mensaje: string }> =>
    apiClient.patch<{ mensaje: string }>(`/api/maquinas/${id}/estado`, { body: { activo } }),
  subirFotoMaquina: (id: number, archivo: File) => subirFoto('maquinas', id, archivo),

  // Lados de máquina (`maquina_lado`)
  listarLados: (signal?: AbortSignal): Promise<LadosRespuesta> =>
    apiClient.get<LadosRespuesta>('/api/lados', { signal }),
  crearLado: (payload: LadoPayload): Promise<{ lado: Lado; mensaje: string }> =>
    apiClient.post<{ lado: Lado; mensaje: string }>('/api/lados', { body: payload }),
  actualizarLado: (id: number, payload: LadoPayload): Promise<{ lado: Lado; mensaje: string }> =>
    apiClient.put<{ lado: Lado; mensaje: string }>(`/api/lados/${id}`, { body: payload }),
  cambiarEstadoLado: (id: number, activo: boolean): Promise<{ mensaje: string }> =>
    apiClient.patch<{ mensaje: string }>(`/api/lados/${id}/estado`, { body: { activo } }),
  subirFotoLado: (id: number, archivo: File) => subirFoto('lados', id, archivo),

  // Mangueras del lado (`maquina_producto`)
  listarManguerasLado: (id: number, signal?: AbortSignal): Promise<ManguerasLadoRespuesta> =>
    apiClient.get<ManguerasLadoRespuesta>(`/api/lados/${id}/productos`, { signal }),
  guardarManguerasLado: (
    id: number,
    productos: MangueraLadoPayload[],
  ): Promise<{ mensaje: string; asignados: number }> =>
    apiClient.put<{ mensaje: string; asignados: number }>(`/api/lados/${id}/productos`, {
      body: { productos },
    }),

  // Mangueras del controlador (`pts_nozzles`)
  listarMangueras: (signal?: AbortSignal): Promise<ManguerasRespuesta> =>
    apiClient.get<ManguerasRespuesta>('/api/mangueras', { signal }),
  guardarMangueras: (
    lados: { pump: number; mangueras: (number | null)[] }[],
  ): Promise<{ lados: ManguerasRespuesta['lados']; mensaje: string }> =>
    apiClient.put<{ lados: ManguerasRespuesta['lados']; mensaje: string }>('/api/mangueras', {
      body: { lados },
    }),
  estadoSincronizacion: (signal?: AbortSignal): Promise<EstadoSincronizacion> =>
    apiClient.get<EstadoSincronizacion>('/api/mangueras/estado', { signal }),
  sincronizar: (): Promise<SincronizacionRespuesta> =>
    apiClient.post<SincronizacionRespuesta>('/api/mangueras/sincronizar', { body: {} }),

  // Automatización de RIC (`pts_ric_automation_config`)
  listarRic: (signal?: AbortSignal): Promise<RicRespuesta> =>
    apiClient.get<RicRespuesta>('/api/ric_automation', { signal }),
  guardarRic: (
    idTanque: number,
    payload: RicPayload,
  ): Promise<{ tanque: TanqueRic; mensaje: string }> =>
    apiClient.put<{ tanque: TanqueRic; mensaje: string }>(`/api/ric_automation/${idTanque}`, {
      body: payload,
    }),
};

/** Lista de máquinas (`/configuracion/maquinas`). */
export function useMaquinas() {
  return useQuery({
    queryKey: avanzadoKeys.maquinas,
    queryFn: ({ signal }) => avanzadoApi.listarMaquinas(signal),
  });
}

/** Alta, edición, estado y foto de máquinas. */
export function useMutarMaquinas() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: avanzadoKeys.maquinas });

  return {
    crear: useMutation({ mutationFn: avanzadoApi.crearMaquina, onSuccess: refrescar }),
    actualizar: useMutation({
      mutationFn: ({ id, payload }: { id: number; payload: MaquinaPayload }) =>
        avanzadoApi.actualizarMaquina(id, payload),
      onSuccess: refrescar,
    }),
    cambiarEstado: useMutation({
      mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
        avanzadoApi.cambiarEstadoMaquina(id, activo),
      onSuccess: refrescar,
    }),
    subirFoto: useMutation({
      mutationFn: ({ id, archivo }: { id: number; archivo: File }) =>
        avanzadoApi.subirFotoMaquina(id, archivo),
      onSuccess: refrescar,
    }),
  };
}

/** Lista de lados de máquina (`/configuracion/lados`). */
export function useLados() {
  return useQuery({ queryKey: avanzadoKeys.lados, queryFn: ({ signal }) => avanzadoApi.listarLados(signal) });
}

/** Alta, edición, estado y foto de lados. */
export function useMutarLados() {
  const cliente = useQueryClient();
  const refrescar = () => {
    void cliente.invalidateQueries({ queryKey: avanzadoKeys.lados });
    void cliente.invalidateQueries({ queryKey: avanzadoKeys.mangueras });
  };

  return {
    crear: useMutation({ mutationFn: avanzadoApi.crearLado, onSuccess: refrescar }),
    actualizar: useMutation({
      mutationFn: ({ id, payload }: { id: number; payload: LadoPayload }) =>
        avanzadoApi.actualizarLado(id, payload),
      onSuccess: refrescar,
    }),
    cambiarEstado: useMutation({
      mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
        avanzadoApi.cambiarEstadoLado(id, activo),
      onSuccess: refrescar,
    }),
    subirFoto: useMutation({
      mutationFn: ({ id, archivo }: { id: number; archivo: File }) =>
        avanzadoApi.subirFotoLado(id, archivo),
      onSuccess: refrescar,
    }),
  };
}

/** Mangueras de un lado (`maquina_producto`), sólo si se abre el diálogo. */
export function useManguerasLado(id: number | null) {
  return useQuery({
    queryKey: avanzadoKeys.manguerasLado(id ?? 0),
    queryFn: ({ signal }) => avanzadoApi.listarManguerasLado(id ?? 0, signal),
    enabled: id !== null,
  });
}

/** Guarda las mangueras de un lado. */
export function useMutarManguerasLado(id: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (productos: MangueraLadoPayload[]) =>
      avanzadoApi.guardarManguerasLado(id ?? 0, productos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: avanzadoKeys.lados });
      void cliente.invalidateQueries({ queryKey: avanzadoKeys.manguerasLado(id ?? 0) });
    },
  });
}

/** Mangueras del controlador (`pts_nozzles`). */
export function useMangueras() {
  return useQuery({
    queryKey: avanzadoKeys.mangueras,
    queryFn: ({ signal }) => avanzadoApi.listarMangueras(signal),
  });
}

/** Guarda las mangueras del controlador (todos los lados de una vez). */
export function useMutarMangueras() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (lados: { pump: number; mangueras: (number | null)[] }[]) =>
      avanzadoApi.guardarMangueras(lados),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: avanzadoKeys.mangueras });
      void cliente.invalidateQueries({ queryKey: avanzadoKeys.estadoSync });
    },
  });
}

/** Diagnóstico de sincronización de los lados del controlador. */
export function useEstadoSincronizacion() {
  return useQuery({
    queryKey: avanzadoKeys.estadoSync,
    queryFn: ({ signal }) => avanzadoApi.estadoSincronizacion(signal),
  });
}

/** Crea los lados del controlador que faltan. */
export function useSincronizar() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: avanzadoApi.sincronizar,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: avanzadoKeys.mangueras });
      void cliente.invalidateQueries({ queryKey: avanzadoKeys.estadoSync });
      void cliente.invalidateQueries({ queryKey: avanzadoKeys.lados });
    },
  });
}

/** Automatización de RIC por tanque. */
export function useRicAutomation() {
  return useQuery({
    queryKey: avanzadoKeys.ric,
    queryFn: ({ signal }) => avanzadoApi.listarRic(signal),
  });
}

/** Guarda la regla de un tanque y refresca el listado. */
export function useMutarRic() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ idTanque, payload }: { idTanque: number; payload: RicPayload }) =>
      avanzadoApi.guardarRic(idTanque, payload),
    onSuccess: () => cliente.invalidateQueries({ queryKey: avanzadoKeys.ric }),
  });
}
